/**
 * Identity, with the marketplace's signature idiom attached.
 *
 * styles.css's `.avatar` carries a soft tertiary halo. DESIGN.md calls that the
 * AVAILABILITY signal, so it has to mean something: `available={false}` turns it
 * off (app.css gates the `::after`), and a card that has no availability concept
 * passes nothing and gets the plain disc.
 */
export type AvatarProps = {
  /** Full name or email; the initials are derived from it. */
  name?: string | null;
  /** Overrides the derived initials. A person's name gives two letters, but a
   *  job title ("Leaking kitchen tap") is not a name and "LT" would be a lie —
   *  those callers pass the single letter they mean. */
  initials?: string;
  src?: string | null;
  size?: "sm" | "md" | "lg";
  /** `undefined` = availability is not a concept here; `false` = not available. */
  available?: boolean;
  className?: string;
};

/** First letters of the first two words, or the first letter of an email. */
export function initialsOf(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const local = trimmed.includes("@") ? (trimmed.split("@")[0] ?? trimmed) : trimmed;
  const words = local.split(/[\s._-]+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  const second = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? "") : "";
  return (first + second).toUpperCase() || "?";
}

export function Avatar({ name, initials, src, size = "md", available, className = "" }: AvatarProps) {
  const classes = ["avatar", size === "sm" ? "avatar--sm" : "", size === "lg" ? "avatar--lg" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      data-available={available ? "true" : "false"}
    >
      {src ? (
        // Decorative: the name is always rendered beside the avatar in every
        // place this is used, so alt text would only repeat it.
        <img src={src} alt="" />
      ) : (
        <span className="avatar__initials" aria-hidden="true">
          {initials ?? initialsOf(name)}
        </span>
      )}
    </span>
  );
}
